import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {catchError, map, Observable, firstValueFrom} from 'rxjs';

import {BasedataService} from '../shared/basedata.service';

import {Companies, Company} from './company';
import {ConfigService} from '@docma-mm-core/config/config.service';
import {Project} from '@docma-mm-core/project/project';

const savedProjectCompaniesKey = 'mm-savedProjectCompanies';
export interface DeleteCompaniesResponse {
  WithReferences: number[];
  Deleted: number[];
}
@Injectable()
export class CompanyService extends BasedataService {

  private projectUrl = '/projects/';
  private companyUrl = '/companies';

  constructor(private http: HttpClient, configService: ConfigService) {
    super(configService);
  }

  private getCompanyUrl() {
    return super.getFullUrl(this.companyUrl);
  }

  private getProjectCompanyUrl(projectId: number) {
    return super.getFullUrl(this.projectUrl + projectId + this.companyUrl);
  }

  private getProjectConfigUrl(projectId: number) {
    return super.getFullUrl(this.projectUrl + projectId + '/configuration');
  }

  // get list of all companies
  getNotAssignedCompanies(project: Project, top: number = 10000, skip: number = 0): Observable<Companies> {
    const endpoint = this.getCompanyUrl() + '/notassigned';
    let httpParams = new HttpParams();
    if (top) { httpParams = httpParams.set('$top', top.toString()); }
    if (skip) { httpParams = httpParams.set('$skip', skip.toString()); }
    if (project?.Id) { httpParams = httpParams.set('projectId', project.Id); }
    // if (onlyGlobalCompanies) httpParams = httpParams.set('$onlyGlobalCompanies', onlyGlobalCompanies.toString())
    httpParams = httpParams.set('$details', '10');
    return this.http
      .get<Companies>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // search componies
  searchCompanies(top: number = 10,
                  skip: number = 0,
                  id: number = null,
                  uid: string = null,
                  name1: string = null,
                  name2: string = null,
                  shortName: string = null,
                  city: string = null,
                  cityPOBox: string = null,
                  country: string = null,
                  email: string = null,
                  fax: string = null,
                  phone: string = null,
                  poBox: string = null,
                  street: string = null,
                  web: string = null,
                  zipCode: string = null,
                  zipCodePOBox: string = null,
                  isActive: boolean = null,
                  isInsolvent: boolean = null,
                  usePOBoxAsAddress: boolean = null,
                  viewpoint: number = null,
                  pid: string = null,
                  puid: string = null): Observable<Companies> {
    const endpoint = this.getCompanyUrl() + '/search';
    let httpParams = new HttpParams();
    if (top) { httpParams = httpParams.set('$top', top.toString()); }
    if (skip) { httpParams = httpParams.set('$skip', skip.toString()); }
    if (id) { httpParams = httpParams.set('id', id.toString()); }
    if (uid) { httpParams = httpParams.set('uid', uid); }
    if (name1) { httpParams = httpParams.set('name1', name1); }
    if (name2) { httpParams = httpParams.set('name2', name2); }
    if (shortName) { httpParams = httpParams.set('shortName', shortName); }
    if (city) { httpParams = httpParams.set('city', city); }
    if (cityPOBox) { httpParams = httpParams.set('cityPOBox', cityPOBox); }
    if (country) { httpParams = httpParams.set('country', country); }
    if (email) { httpParams = httpParams.set('email', email); }
    if (fax) { httpParams = httpParams.set('fax', fax); }
    if (phone) { httpParams = httpParams.set('phone', phone); }
    if (poBox) { httpParams = httpParams.set('poBox', poBox); }
    if (street) { httpParams = httpParams.set('street', street); }
    if (web) { httpParams = httpParams.set('web', web); }
    if (zipCode) { httpParams = httpParams.set('zipCode', zipCode); }
    if (zipCodePOBox) { httpParams = httpParams.set('zipCodePOBox', zipCodePOBox); }
    if (isActive) { httpParams = httpParams.set('isActive', isActive ? 'true' : 'false'); }
    if (isInsolvent) { httpParams = httpParams.set('isInsolvent', isInsolvent ? 'true' : 'false'); }
    if (usePOBoxAsAddress) { httpParams = httpParams.set('usePOBoxAsAddress', usePOBoxAsAddress ? 'true' : 'false'); }
    if (viewpoint) { httpParams = httpParams.set('viewpoint', viewpoint.toString()); }
    if (pid) { httpParams = httpParams.set('pid', pid); }
    if (puid) { httpParams = httpParams.set('puid', puid); }
    return this.http
      .get<Companies>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get company by id
  getCompanyById(companyId: number): Observable<Company> {
    const endpoint = this.getCompanyUrl() + '/' + companyId;
    return this.http
      .get<Company>(endpoint, { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get company by uid
  getCompanyByUid(companyUid: string): Observable<Company> {
    const endpoint = this.getCompanyUrl();
    let httpParams = new HttpParams();
    httpParams = httpParams.set('uid', companyUid);
    return this.http
      .get<Company>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // create new company
  createCompany(company: Company): Observable<Company> {
    const endpoint = this.getCompanyUrl();
    return this.http
      .post<Company>(endpoint, JSON.stringify(company), { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // update existing company (global + project company!)
  updateCompany(company: Company, companyId: number): Observable<Company> {
    const endpoint = this.getCompanyUrl() + '\\' + companyId;
    return this.http
      .put<Company>(endpoint, JSON.stringify(company), { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get companies by project
  getProjectCompanies(projectId: number, top: number = 10, skip: number = 0): Observable<Companies> {
    const endpoint = this.getProjectCompanyUrl(projectId);
    let httpParams = new HttpParams();
    if (top) { httpParams = httpParams.set('$top', top.toString()); }
    if (skip) { httpParams = httpParams.set('$skip', skip.toString()); }
    httpParams = httpParams.set('$details', '10');
    httpParams = httpParams.set('returnWithoutFilterIfNoPerms', true);
    httpParams = httpParams.set('minimalResultIfNoPerms', true);

    return this.http
      .get<Companies>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get company by id
  getProjectCompanyById(projectId: number, companyId: number): Observable<Company> {
    const endpoint = this.getProjectCompanyUrl(projectId) + '/' + companyId;
    return this.http
      .get<Company>(endpoint, { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // get company by uid
  getProjectCompanyByUid(projectId: number, companyUid: string): Observable<Company> {
    const endpoint = this.getProjectCompanyUrl(projectId);
    let httpParams = new HttpParams();
    httpParams = httpParams.set('uid', companyUid);
    return this.http
      .get<Company>(endpoint, { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // create new company
  createProjectCompany(projectId: number, company: Company): Observable<Company> {
    const endpoint = this.getProjectCompanyUrl(projectId);
    return this.http
      .post<Company>(endpoint, JSON.stringify(company), { headers: this.getHeaders() })
      .pipe(catchError(e  => this.handleError(e)));
  }

  // assign existing company
  assignProjectCompany(projectId: number, company: Company): Observable<Company> {
    const endpoint = this.getProjectCompanyUrl(projectId) + '/' + company.Id;

    const httpParams = new HttpParams({fromString: 'includeContacts=true&includeOrders=true'});

    return this.http
      .put<Company>(endpoint, JSON.stringify(company), { headers: this.getHeaders(), params: httpParams })
      .pipe(catchError(e  => this.handleError(e)));
  }

    // update existing companies
  assignProjectCompanies(projectId: number, companies: Company[]): Observable<Company[]> {
    const companyIds: { Id: number }[] = companies.map(company => ({ Id: company.Id }));

    const endpoint = this.getProjectCompanyUrl(projectId) + '/';

    const httpParams = new HttpParams({fromString: 'includeContacts=true&includeOrders=true'});

    return this.http
        .put<Company[]>(endpoint, JSON.stringify(companyIds), { headers: this.getHeaders(), params: httpParams })
        .pipe(catchError(e  => this.handleError(e)));
  }

deleteCompanies(projectId: number, companyIds: number[]): Observable<DeleteCompaniesResponse> {
  const endpoint = this.getProjectConfigUrl(projectId) + '/companies';
  return this.http
    .delete<DeleteCompaniesResponse>(endpoint, {
      headers: this.getHeaders(),
      body: companyIds
    })
    .pipe(catchError(e => this.handleError(e)));
}

  releaseFromGlobal(projectId: number, companiesIds: number[]): Observable<boolean> {
    const endpoint = this.getProjectConfigUrl(projectId) + '/companies/release';
    return this.http
      .post<void>(endpoint, companiesIds, { headers: this.getHeaders() })
      .pipe(
        catchError(e => this.handleError(e)),
        map(() => true)
      );
  }
}
